import { create, StateCreator } from 'zustand';
import { getNotifications, markNotificationAsRead } from '../services/notification.service';
import { NotificationSchema, GetNotificationsParams } from '../types/notification.type';

interface NotificationState {
  notifications: NotificationSchema[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  
  // Actions
  fetchNotifications: (params: GetNotificationsParams, append?: boolean) => Promise<void>;
  markAsRead: (organization_id: string, notification_id: string) => Promise<void>;
  clearNotifications: () => void;
  updateUnreadCount: () => void;
}

export const useNotificationStore = create<NotificationState>(((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  hasMore: true,
  totalCount: 0,

  fetchNotifications: async (params: GetNotificationsParams, append = false) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getNotifications(params);
      
      if (append) {
        set(state => ({
          notifications: [...state.notifications, ...response.result_list],
          totalCount: response.total_count,
          hasMore: response.result_list.length === params.limit
        }));
      } else {
        set({
          notifications: response.result_list,
          totalCount: response.total_count,
          hasMore: response.result_list.length === params.limit
        });
      }
      
      // Update unread count
      get().updateUnreadCount();
    } catch (error: any) {
      set({ 
        error: error.response?.data?.message || error.message || 'Failed to fetch notifications',
        isLoading: false 
      });
    } finally {
      set({ isLoading: false });
    }
  },

  markAsRead: async (organization_id: string, notification_id: string) => {
    try {
      await markNotificationAsRead(organization_id, notification_id);
      
      // Update the notification in the store
      set(state => ({
        notifications: state.notifications.map(notification =>
          notification.id === notification_id
            ? { ...notification, is_read: true }
            : notification
        )
      }));
      
      // Update unread count
      get().updateUnreadCount();
    } catch (error: any) {
      set({ 
        error: error.response?.data?.message || error.message || 'Failed to mark notification as read'
      });
    }
  },

  clearNotifications: () => {
    set({
      notifications: [],
      unreadCount: 0,
      hasMore: true,
      totalCount: 0,
      error: null
    });
  },

  updateUnreadCount: () => {
    set(state => ({
      unreadCount: state.notifications.filter(notification => !notification.is_read).length
    }));
  }
})) as StateCreator<NotificationState>); 